import { DeepKeyPaths, DoNotCare, KeyPaths } from "../types";
import { isValidElement, ReactNode, useEffect, useMemo, useState } from "react";
import Button from "../Button";
import { styles } from "./styles";
import { TreeNodeProps, TreeProps } from "./types";
import {
  camelToCapitalizedWords,
  defaultLookupLabel as kitDefaultLookupLabel,
  toReadonly
} from "../../utils";
import { DetailsSummary } from "../DetailsSummary";
import {
  isArray,
  isMap,
  isNullOrUndefined,
  isRecord,
  isSet,
  isString
} from "../../types/guards";

/**
 * `Tree` is a recursive component that renders a nested data structure
 * (objects and arrays) as an expandable tree view.
 */
function Tree<T extends { [k in keyof T]: unknown } | unknown[]>({
  data,
  renderValue,
  lookupVisible,
  lookupActions,
  lookupValue,
  lookupLabel = defaultLookupLabel,
  lookupArrayLabel = () => kitDefaultLookupLabel,
  lookupPrefix,
  parentKeyPath,
  showKeys = 10,
  expandedKeys = [],
  fixedKeys = [],
  excludeEmpty = false
}: TreeProps<T>) {
  const topLevelExpandedKeys = useMemo(
    () =>
      [
        ...new Set(expandedKeys.map(key => String(key).split(".")[0]))
      ] as KeyPaths<T>[],
    [expandedKeys, parentKeyPath]
  );

  const minShowKeys = Math.max(
    showKeys,
    topLevelExpandedKeys.length + fixedKeys.length
  );

  const [showKeyCount, setShowKeyCount] = useState(minShowKeys);

  useEffect(() => {
    setShowKeyCount(prev => Math.max(prev, minShowKeys));
  }, [minShowKeys]);

  const allKeys = useMemo(
    () => [
      ...new Set(
        [...fixedKeys, ...topLevelExpandedKeys, ...Object.keys(data)].filter(
          key => {
            const value = getValue(key);
            return shouldRender(value, {
              parentKeyPath: makeKeyPath<T>(key, parentKeyPath) as string,
              lookupVisible,
              excludeEmpty
            });
          }
        )
      )
    ],
    [
      data,
      lookupVisible,
      lookupValue,
      parentKeyPath,
      fixedKeys,
      topLevelExpandedKeys,
      excludeEmpty
    ]
  );

  function getValue(key: string) {
    const value = Array.isArray(data)
      ? data[Number(key)]
      : data[key as keyof T];
    if (lookupValue) {
      const keyPath = makeKeyPath<T>(key, parentKeyPath);
      const lookupFn = lookupValue(keyPath);
      if (lookupFn) {
        return lookupFn(value);
      }
    }
    return value;
  }

  const visibleKeys = useMemo(
    () => allKeys.slice(0, showKeyCount),
    [allKeys, showKeyCount]
  );

  const moreKeysCount = useMemo(
    () => allKeys.length - visibleKeys.length,
    [allKeys, visibleKeys]
  );

  function handleShowMore() {
    setShowKeyCount(prev => prev + showKeys);
  }

  return (
    <ul className="kit-Tree" css={[styles.list.base]}>
      {visibleKeys.map(key => {
        const keyPath = makeKeyPath<T>(key, parentKeyPath);
        return (
          <TreeNode
            key={key}
            label={
              Array.isArray(data)
                ? lookupArrayLabel(keyPath)(data[Number(key)])
                : lookupLabel(keyPath)
            }
            value={getValue(key)}
            treeProps={{
              renderValue,
              lookupValue,
              lookupActions,
              lookupVisible,
              expandedKeys,
              parentKeyPath: keyPath,
              lookupLabel,
              lookupPrefix,
              lookupArrayLabel,
              showKeys,
              fixedKeys,
              excludeEmpty
            }}
          />
        );
      })}
      {moreKeysCount > 0 && (
        <li>
          <Button
            onClick={handleShowMore}
            prefix="Plus"
            variant="ghost"
          >{`${moreKeysCount} more`}</Button>
        </li>
      )}
    </ul>
  );
}

/**
 * Component that renders a single node in the tree (label and value).
 * If the node is expandable (object or array), it renders a `DetailsSummary` component that can be toggled to show/hide the nested content.
 */
function TreeNode<T>({
  label,
  value,
  isExpanded: _isExpanded,
  treeProps
}: TreeNodeProps<T>) {
  const renderFn = treeProps.renderValue?.(
    treeProps.parentKeyPath as DeepKeyPaths<T>
  );
  const prefix = treeProps.lookupPrefix?.(
    treeProps.parentKeyPath as DeepKeyPaths<T>
  );
  const renderedValue = renderFn ? renderFn(value) : value;
  const isExpandable =
    !isValidElement(renderedValue) &&
    (isRecord(renderedValue) || Array.isArray(renderedValue)) &&
    !isEmpty(renderedValue);

  const [isExpanded, setIsExpanded] = useState(
    _isExpanded ??
      (!isExpandable ||
        !treeProps.parentKeyPath ||
        treeProps.expandedKeys?.some(
          k =>
            typeof k === "string" &&
            (k === treeProps.parentKeyPath ||
              k?.startsWith(`${treeProps.parentKeyPath}.`))
        ) ||
        (!isRecord(value) && !Array.isArray(value)))
  );

  const isVisible = useMemo(
    () => shouldRender(value, treeProps),
    [
      value,
      treeProps.parentKeyPath,
      treeProps.lookupVisible,
      treeProps.excludeEmpty
    ]
  );

  if (!isVisible) {
    return null;
  }

  const labelElement: ReactNode = label ? (
    <span css={[styles.node.label]}>
      {prefix}
      {label}
      {": "}
    </span>
  ) : null;

  const node = (
    <TreeNodeValue
      value={value}
      isExpanded={!!isExpanded}
      treeProps={treeProps}
    />
  );
  return (
    <li css={[styles.node.base]}>
      {isExpandable && (
        <DetailsSummary
          title={labelElement}
          isOpen={isExpanded}
          onToggle={setIsExpanded}
          actions={treeProps.lookupActions?.(
            treeProps.parentKeyPath as DeepKeyPaths<T>
          )?.(value)}
          variant="inline"
        >
          {node}
        </DetailsSummary>
      )}
      {!isExpandable && (
        <>
          {labelElement}
          {node}
        </>
      )}
    </li>
  );
}

/**
 * Component that renders the value of a tree node, and if the value is expandable (object or array), it renders a nested `Tree` component.
 * If the value is not expandable, it uses the `renderValue` function from `treeProps` to render the value, or falls back to `toReadonly` for primitive values.
 */
function TreeNodeValue<T>({
  value,
  isExpanded,
  treeProps
}: Omit<TreeNodeProps<T>, "label">) {
  if (!isExpanded) {
    return null;
  }
  let renderFn = treeProps.renderValue?.(
    treeProps.parentKeyPath as DeepKeyPaths<T>
  );

  if (!renderFn) {
    if (isRecord.notEmpty(value) || isArray.notEmpty(value)) {
      return <Tree {...treeProps} data={value} fixedKeys={[]} />;
    }
    renderFn = toReadonly;
  }

  const actions = treeProps.lookupActions?.(
    treeProps.parentKeyPath as DeepKeyPaths<T>
  );

  return (
    <div css={[styles.node.value]}>
      <strong>{renderFn(value)}</strong>
      {actions && <span>{actions(value)}</span>}
    </div>
  );
}

/**
 * Check whether a value is considered "empty" for the purposes of rendering in the tree.
 * Empty values include values that produce no meaningful output when rendered,
 * such as null, undefined, empty strings, empty arrays, empty objects, and empty sets/maps.
 */
function isEmpty(value: unknown): boolean {
  return (
    isNullOrUndefined(value) ||
    isString.empty(value, { trimmed: true }) ||
    isArray.empty(value) ||
    isSet.empty(value) ||
    isRecord.empty(value) ||
    isMap.empty(value) ||
    typeof value === "function" ||
    typeof value === "symbol"
  );
}

/**
 * Determine whether a tree node should be rendered — based on its visibility and emptiness.
 */
function shouldRender(
  value: unknown,
  {
    parentKeyPath,
    lookupVisible,
    excludeEmpty
  }: Pick<
    TreeNodeProps<DoNotCare>["treeProps"],
    "lookupVisible" | "excludeEmpty" | "parentKeyPath"
  > = {}
): boolean {
  const lookupVisibleResult = lookupVisible?.(parentKeyPath ?? "", value);
  if (lookupVisibleResult === false) {
    return false;
  }
  if (excludeEmpty && isEmpty(value)) {
    return false;
  }
  return lookupVisibleResult ?? true;
}

function makeKeyPath<T>(key: string, parentKeyPath: unknown) {
  if (typeof parentKeyPath === "string") {
    return `${parentKeyPath}.${key}` as DeepKeyPaths<T>;
  }
  return key as DeepKeyPaths<T>;
}

function defaultLookupLabel(keyPath: string) {
  const lastSegment = String(keyPath).split(".").pop();
  return lastSegment ? camelToCapitalizedWords(lastSegment) : "";
}

export default Tree;
Tree.defaultLookupLabel = defaultLookupLabel;
